import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '@/api/axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Plus, Trash, PencilSimple, Check, X, Calendar, Tag, Users as UsersIcon, User,
  CheckCircle, Circle, Note as NoteIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const PRESET_TAGS = ['urgente', 'seguimiento', 'idea', 'cliente', 'producto', 'personal'];

const TAG_COLORS = {
  urgente: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200',
  seguimiento: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200',
  idea: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200',
  cliente: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200',
  producto: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200',
  personal: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300 border-gray-200',
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const dueStatus = (date, completed) => {
  if (!date || completed) return null;
  const today = todayISO();
  if (date < today) return { label: 'Vencida', color: 'bg-red-500 text-white' };
  if (date === today) return { label: 'Hoy', color: 'bg-amber-500 text-white' };
  return null;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short' });
};

const emptyDraft = (scope = 'private') => ({
  title: '', content: '', scope, completed: false, due_date: '', tags: [],
});

const NoteCard = ({ note, onToggle, onEdit, onDelete, canShare }) => {
  const due = dueStatus(note.due_date, note.completed);
  return (
    <div
      className={`p-3 rounded-lg border transition-all ${note.completed ? 'bg-[var(--surface-muted)] opacity-60' : 'bg-[var(--surface)]'} border-[var(--border)] hover:border-[var(--text-muted)]`}
      data-testid={`note-${note.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          onClick={() => onToggle(note)}
          className="flex-shrink-0 mt-0.5 text-[var(--text-muted)] hover:text-emerald-600 transition"
          data-testid={`toggle-${note.id}`}
        >
          {note.completed
            ? <CheckCircle size={20} weight="fill" className="text-emerald-600" />
            : <Circle size={20} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className={`text-sm font-bold text-[var(--text)] break-words ${note.completed ? 'line-through' : ''}`}>{note.title}</h4>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(note)} data-testid={`edit-${note.id}`}>
                <PencilSimple size={14} />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => onDelete(note)} data-testid={`delete-${note.id}`}>
                <Trash size={14} />
              </Button>
            </div>
          </div>
          {note.content && (
            <p className={`text-xs text-[var(--text-muted)] mt-1 whitespace-pre-wrap break-words ${note.completed ? 'line-through' : ''}`}>{note.content}</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {due && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${due.color}`}>{due.label}</span>
            )}
            {note.due_date && !due && (
              <span className="text-[10px] text-[var(--text-muted)] inline-flex items-center gap-0.5">
                <Calendar size={10} />{formatDate(note.due_date)}
              </span>
            )}
            {(note.tags || []).map((t) => (
              <Badge key={t} variant="outline" className={`text-[10px] px-1.5 py-0 h-auto ${TAG_COLORS[t] || 'bg-slate-100 text-slate-700'}`}>{t}</Badge>
            ))}
            {note.scope === 'shared' && canShare && (
              <span className="text-[10px] text-[var(--text-muted)] inline-flex items-center gap-0.5 ml-auto">
                <UsersIcon size={10} /> {note.creator_name || '—'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const NoteEditor = ({ initial, onSave, onCancel, allowShared = true, defaultScope = 'private' }) => {
  const [draft, setDraft] = useState(initial || emptyDraft(defaultScope));
  const toggleTag = (t) => setDraft(d => ({
    ...d, tags: d.tags.includes(t) ? d.tags.filter(x => x !== t) : [...d.tags, t],
  }));
  const submit = () => {
    if (!draft.title.trim()) {
      toast.error('Escribe un título');
      return;
    }
    onSave({ ...draft, title: draft.title.trim(), content: draft.content?.trim() || '' });
  };
  return (
    <div className="space-y-2 p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)]" data-testid="note-editor">
      <Input
        placeholder="Título del pendiente…"
        value={draft.title}
        onChange={e => setDraft({ ...draft, title: e.target.value })}
        className="font-bold text-sm"
        autoFocus
        data-testid="note-title-input"
      />
      <Textarea
        placeholder="Descripción / detalles (opcional)…"
        value={draft.content || ''}
        onChange={e => setDraft({ ...draft, content: e.target.value })}
        rows={3}
        className="text-xs resize-none"
        data-testid="note-content-input"
      />
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 text-xs">
          <Calendar size={14} className="text-[var(--text-muted)]" />
          <Input
            type="date"
            value={draft.due_date || ''}
            onChange={e => setDraft({ ...draft, due_date: e.target.value })}
            className="h-8 text-xs w-40"
            data-testid="note-due-input"
          />
        </div>
        {allowShared && (
          <div className="inline-flex items-center gap-1 ml-auto text-xs">
            <button
              type="button"
              onClick={() => setDraft({ ...draft, scope: draft.scope === 'shared' ? 'private' : 'shared' })}
              className={`px-2 py-1 rounded text-xs font-bold inline-flex items-center gap-1 transition ${draft.scope === 'shared' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-[var(--surface-muted)] text-[var(--text-muted)]'}`}
              data-testid="note-scope-toggle"
            >
              {draft.scope === 'shared' ? <><UsersIcon size={12} /> Equipo</> : <><User size={12} /> Privada</>}
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1 pt-1">
        <Tag size={12} className="text-[var(--text-muted)]" />
        {PRESET_TAGS.map(t => (
          <button
            key={t}
            type="button"
            onClick={() => toggleTag(t)}
            className={`text-[10px] px-1.5 py-0.5 rounded border ${draft.tags.includes(t) ? TAG_COLORS[t] || '' : 'bg-transparent text-[var(--text-muted)] border-[var(--border)]'}`}
            data-testid={`tag-${t}`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="note-cancel-btn">
          <X size={14} /> Cancelar
        </Button>
        <Button size="sm" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="note-save-btn">
          <Check size={14} /> Guardar
        </Button>
      </div>
    </div>
  );
};

export default function Pendientes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');           // all | private | shared
  const [showCompleted, setShowCompleted] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterTag, setFilterTag] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notes', { params: { scope: tab, show_completed: showCompleted } });
      setNotes(data || []);
    } catch (e) {
      toast.error('No se pudieron cargar los pendientes');
    } finally {
      setLoading(false);
    }
  }, [tab, showCompleted]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    let list = notes;
    if (filterTag) list = list.filter(n => (n.tags || []).includes(filterTag));
    return list;
  }, [notes, filterTag]);

  const counts = useMemo(() => ({
    total: notes.length,
    pending: notes.filter(n => !n.completed).length,
    overdue: notes.filter(n => !n.completed && n.due_date && n.due_date < todayISO()).length,
  }), [notes]);

  const allTags = useMemo(() => {
    const m = new Map();
    notes.forEach(n => (n.tags || []).forEach(t => m.set(t, (m.get(t) || 0) + 1)));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [notes]);

  const handleSave = async (draft) => {
    try {
      if (editingId) {
        await api.put(`/notes/${editingId}`, draft);
        toast.success('Pendiente actualizado');
      } else {
        await api.post('/notes', draft);
        toast.success('Pendiente creado');
      }
      setCreating(false);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`¿Eliminar "${n.title}"?`)) return;
    try {
      await api.delete(`/notes/${n.id}`);
      toast.success('Eliminado');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const handleToggle = async (n) => {
    try {
      await api.patch(`/notes/${n.id}/toggle`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4" data-testid="pendientes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-black text-[var(--text)] flex items-center gap-2">
            <NoteIcon size={26} weight="duotone" className="text-blue-600" /> Pendientes
          </h1>
          <p className="text-xs text-[var(--text-muted)]">
            {counts.pending} sin completar · {counts.overdue > 0 && <span className="text-red-600 font-bold">{counts.overdue} vencidas</span>}{counts.overdue === 0 && 'al día'}
          </p>
        </div>
        <Button onClick={() => { setCreating(true); setEditingId(null); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-note-btn">
          <Plus size={16} /> Nuevo
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[var(--surface-muted)]">
          <TabsTrigger value="all" data-testid="tab-all">Todas ({notes.length})</TabsTrigger>
          <TabsTrigger value="private" data-testid="tab-private"><User size={12} className="mr-1" /> Privadas</TabsTrigger>
          <TabsTrigger value="shared" data-testid="tab-shared"><UsersIcon size={12} className="mr-1" /> Equipo</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <button
              onClick={() => setShowCompleted(v => !v)}
              className={`px-2 py-1 rounded ${showCompleted ? 'bg-[var(--surface-muted)] text-[var(--text)]' : 'bg-blue-100 text-blue-700'}`}
              data-testid="toggle-completed-filter"
            >
              {showCompleted ? 'Ocultar completadas' : 'Mostrar todas'}
            </button>
            {allTags.length > 0 && (
              <>
                <span className="text-[var(--text-muted)] ml-2">Filtrar por etiqueta:</span>
                {allTags.map(([t, c]) => (
                  <button
                    key={t}
                    onClick={() => setFilterTag(filterTag === t ? null : t)}
                    className={`px-1.5 py-0.5 rounded text-[10px] border ${filterTag === t ? TAG_COLORS[t] || 'bg-blue-100' : 'bg-transparent text-[var(--text-muted)] border-[var(--border)]'}`}
                    data-testid={`filter-${t}`}
                  >
                    {t} ({c})
                  </button>
                ))}
                {filterTag && (
                  <button onClick={() => setFilterTag(null)} className="text-[10px] text-blue-600 underline">Limpiar</button>
                )}
              </>
            )}
          </div>

          {creating && (
            <NoteEditor
              defaultScope={tab === 'shared' ? 'shared' : 'private'}
              onSave={handleSave}
              onCancel={() => setCreating(false)}
            />
          )}

          {loading ? (
            <Card className="p-6 text-center text-sm text-[var(--text-muted)]">Cargando…</Card>
          ) : visible.length === 0 ? (
            <Card className="p-8 text-center">
              <NoteIcon size={36} className="mx-auto text-[var(--text-muted)] mb-2" />
              <p className="text-sm text-[var(--text-muted)]">Sin pendientes aún. ¡Crea el primero!</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {visible.map(n => (
                editingId === n.id ? (
                  <NoteEditor
                    key={n.id}
                    initial={n}
                    onSave={handleSave}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <NoteCard
                    key={n.id}
                    note={n}
                    onToggle={handleToggle}
                    onEdit={(nn) => { setEditingId(nn.id); setCreating(false); }}
                    onDelete={handleDelete}
                    canShare
                  />
                )
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
